import axios from "axios";

/**
 * Create a new chat session for subject management analysis
 * @param {string} userId - User ID
 * @param {string} title - Session title
 * @param {string} systemPrompt - Custom system prompt for the session
 * @param {Object} metadata - Additional metadata
 * @returns {Promise} Session creation response
 */

const createSession = (userId, title = 'Subject Management Session', systemPrompt = null, metadata = {}) =>
  axios.post("api/v1/reports-ai/sessions", {
    userId,
    title,
    systemPrompt,
    metadata: {
      purpose: 'subject-management-assistant',
      ...metadata
    }
  });

/**
 * List chat sessions for a user
 * @param {string} userId - User ID
 * @param {Object} params - Query params (limit, offset, status)
 * @returns {Promise} Sessions list
 */
const listSessions = (userId, params = {}) =>
  axios.get("api/v1/reports-ai/sessions", {
    params: {
      userId,
      limit: 50,
      offset: 0,
      purpose: 'subject-management-assistant',
      ...params,
    },
  });

/**
 * Send a message to the subject management AI assistant
 * @param {string} sessionId - Chat session ID
 * @param {string} content - User message
 * @param {string} userId - User ID
 * @param {Object} subjectContext - Current subject management context (route, filters, etc.)
 * @param {Object} options - Additional options
 * @param {number} options.maxRecentMessages - Maximum recent messages to include
 * @param {string} options.requestId - Request ID for progress tracking
 * @param {AbortSignal} options.signal - AbortController signal for cancellation
 * @param {Object} options.contextMetadata - Context metadata (contextType, identifier, etc.)
 * @returns {Promise} Assistant response
 */
const sendMessage = (sessionId, content, userId, subjectContext = {}, options = {}) =>
  axios.post(`api/v1/reports-ai/sessions/${sessionId}/messages`, {
    content,
    userId,
    reportContext: subjectContext,
    maxRecentMessages: options.maxRecentMessages !== undefined ? options.maxRecentMessages : 10,
    requestId: options.requestId,
    contextMetadata: options.contextMetadata,
  }, {
    signal: options.signal,
  });

/**
 * Get conversation history for a session
 * @param {string} sessionId - Chat session ID
 * @param {Object} params - Query parameters
 * @returns {Promise} Conversation history
 */
const getMessages = (sessionId, params = {}) =>
  axios.get(`api/v1/reports-ai/sessions/${sessionId}/messages`, {
    params: {
      includeInvalidated: false,
      limit: 100,
      offset: 0,
      ...params
    }
  });

/**
 * Get session details
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @returns {Promise} Session details
 */
const getSession = (sessionId, userId) =>
  axios.get(`api/v1/reports-ai/sessions/${sessionId}`, {
    params: { userId }
  });

/**
 * Update session metadata
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @param {Object} updates - Updates (title, status, metadata)
 * @returns {Promise} Updated session
 */
const updateSession = (sessionId, userId, updates = {}) =>
  axios.patch(`api/v1/reports-ai/sessions/${sessionId}`, {
    userId,
    ...updates
  });

/**
 * Delete/archive session
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @param {boolean} permanent - Whether to permanently delete
 * @returns {Promise} Delete confirmation
 */
const deleteSession = (sessionId, userId, permanent = false) =>
  axios.delete(`api/v1/reports-ai/sessions/${sessionId}`, {
    params: { userId, permanent }
  });

/**
 * Get progress updates URL for SSE connection
 * @param {string} requestId - Request ID
 * @returns {string} SSE endpoint URL
 */
const getProgressStreamUrl = (requestId) =>
  `api/v1/reports-ai/progress/${requestId}/stream`;

/**
 * Get current progress status (polling)
 * @param {string} requestId - Request ID
 * @returns {Promise} Progress status
 */
const getProgress = (requestId) =>
  axios.get(`api/v1/reports-ai/progress/${requestId}`);

/**
 * Export session as PDF
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @returns {Promise<Blob>} PDF blob
 */
const exportSession = async (sessionId, userId) => {
  const response = await axios.get(`api/v1/reports-ai/sessions/${sessionId}/export`, {
    params: { userId },
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Generate a descriptive title for the session based on conversation content
 * @param {string} sessionId - Chat session ID
 * @param {string} userId - User ID
 * @returns {Promise} Generated title
 */
const generateTitle = (sessionId, userId) =>
  axios.post(`api/v1/reports-ai/sessions/${sessionId}/generate-title`, {
    userId
  });

/**
 * Send a message with file attachments to the subject management AI assistant
 * @param {string} sessionId - Chat session ID
 * @param {string} content - User message
 * @param {string} userId - User ID
 * @param {Object} subjectContext - Current subject management context
 * @param {File[]} files - Array of File objects to upload (PDF, DOCX, HTML, TXT, images)
 * @param {Object} options - Additional options
 * @returns {Promise} Assistant response with attachment info
 */
const sendMessageWithAttachments = (sessionId, content, userId, subjectContext = {}, files = [], options = {}) => {
  const formData = new FormData();

  formData.append('content', content);
  formData.append('userId', userId);

  if (subjectContext) {
    formData.append('reportContext', JSON.stringify(subjectContext));
  }

  if (options.maxRecentMessages !== undefined) {
    formData.append('maxRecentMessages', options.maxRecentMessages.toString());
  }

  if (options.requestId) {
    formData.append('requestId', options.requestId);
  }

  if (options.contextMetadata) {
    formData.append('contextMetadata', JSON.stringify(options.contextMetadata));
  }

  files.forEach((file) => {
    formData.append('files', file);
  });

  return axios.post(
    `api/v1/reports-ai/sessions/${sessionId}/messages/with-attachments`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: options.signal,
    }
  );
};

const ApiSubjectManagementAi = {
  createSession,
  listSessions,
  sendMessage,
  sendMessageWithAttachments,
  getMessages,
  getSession,
  updateSession,
  deleteSession,
  getProgressStreamUrl,
  getProgress,
  exportSession,
  generateTitle,
};

export default ApiSubjectManagementAi;
